import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useBuyerAuth } from '../../../contexts/BuyerAuthContext';

const ProfileTab = ({ language }) => {
  const { userProfile, updateProfile } = useBuyerAuth();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    companyName: '',
    country: '',
    businessLicenseNumber: '',
    importLicenseNumber: ''
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const content = {
    en: {
      title: 'Profile',
      edit: 'Edit Profile',
      save: 'Save Changes',
      cancel: 'Cancel',
      saving: 'Saving...',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone Number',
      companyName: 'Company Name',
      country: 'Country',
      businessLicense: 'Business License Number',
      importLicense: 'Import License Number',
      accountType: 'Account Type',
      preferredLanguage: 'Preferred Language',
      memberSince: 'Member Since',
      successMessage: 'Profile updated successfully!'
    },
    zh: {
      title: '个人资料',
      edit: '编辑资料',
      save: '保存更改',
      cancel: '取消',
      saving: '保存中...',
      fullName: '姓名',
      email: '电子邮件',
      phone: '电话号码',
      companyName: '公司名称',
      country: '国家',
      businessLicense: '营业执照号码',
      importLicense: '进口许可证号码',
      accountType: '账户类型',
      preferredLanguage: '首选语言',
      memberSince: '成员起始日期',
      successMessage: '资料更新成功！'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    if (userProfile) {
      setFormData({
        fullName: userProfile?.full_name || '',
        phone: userProfile?.phone || '',
        companyName: userProfile?.company_name || '',
        country: userProfile?.country || '',
        businessLicenseNumber: userProfile?.business_license_number || '',
        importLicenseNumber: userProfile?.import_license_number || ''
      });
    }
  }, [userProfile]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateProfile({
        full_name: formData?.fullName,
        phone: formData?.phone,
        company_name: formData?.companyName,
        country: formData?.country,
        business_license_number: formData?.businessLicenseNumber,
        import_license_number: formData?.importLicenseNumber
      });
      setSuccess(true);
      setEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-foreground">{t?.title}</h2>
        {!editing && (
          <Button
            onClick={() => setEditing(true)}
            variant="outline"
            iconName="Edit"
          >
            {t?.edit}
          </Button>
        )}
      </div>

      {success && (
        <div className="p-4 bg-success/10 border border-success rounded-md flex items-center gap-2">
          <Icon name="CheckCircle" size={20} color="var(--color-success)" />
          <p className="text-success">{t?.successMessage}</p>
        </div>
      )}

      <div className="bg-muted/30 rounded-lg p-6 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {editing ? (
            <>
              <Input
                label={t?.fullName}
                name="fullName"
                value={formData?.fullName}
                onChange={handleChange}
              />
              <Input
                label={t?.phone}
                name="phone"
                value={formData?.phone}
                onChange={handleChange}
              />
              <Input
                label={t?.companyName}
                name="companyName"
                value={formData?.companyName}
                onChange={handleChange}
              />
              <Input
                label={t?.country}
                name="country"
                value={formData?.country}
                onChange={handleChange}
              />
              <Input
                label={t?.businessLicense}
                name="businessLicenseNumber"
                value={formData?.businessLicenseNumber}
                onChange={handleChange}
              />
              <Input
                label={t?.importLicense}
                name="importLicenseNumber"
                value={formData?.importLicenseNumber}
                onChange={handleChange}
              />
            </>
          ) : (
            <>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.fullName}</p>
                <p className="font-medium">{userProfile?.full_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.email}</p>
                <p className="font-medium">{userProfile?.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.phone}</p>
                <p className="font-medium">{userProfile?.phone || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.companyName}</p>
                <p className="font-medium">{userProfile?.company_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.country}</p>
                <p className="font-medium">{userProfile?.country || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.accountType}</p>
                <p className="font-medium">{userProfile?.account_type || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.businessLicense}</p>
                <p className="font-medium">{userProfile?.business_license_number || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.importLicense}</p>
                <p className="font-medium">{userProfile?.import_license_number || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.preferredLanguage}</p>
                <p className="font-medium">{userProfile?.preferred_language === 'zh' ? '中文' : 'English'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t?.memberSince}</p>
                <p className="font-medium">{new Date(userProfile?.created_at)?.toLocaleDateString()}</p>
              </div>
            </>
          )}
        </div>

        {editing && (
          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleSave}
              loading={saving}
              iconName="Save"
            >
              {saving ? t?.saving : t?.save}
            </Button>
            <Button
              onClick={() => setEditing(false)}
              variant="outline"
            >
              {t?.cancel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileTab;